"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import {
  createConversation,
  getConversation,
  streamMessage,
  type AgentContext,
} from "@/lib/api/recruiter";
import { getShortlist } from "@/lib/api/shortlists";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatMessage {
  /** Temporary client-side ID (e.g. "user-<ts>") or server message ID */
  id: string;
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean;
}

export interface ShortlistCandidate {
  person_entity_id: number;
  /** ShortlistItem.id (DB row ID) -- used for rating API calls */
  item_id?: number;
  name: string;
  headline?: string;
  keywords?: string[];
  quality_score?: number;
  rationale?: string;
  rating?: string;
}

export type ConnectionState = "idle" | "connecting" | "streaming" | "error";

export interface SearchProgressEntry {
  query: string;
  status: "searching" | "completed";
  count?: number;
}

export interface UseRecruiterChatReturn {
  messages: ChatMessage[];
  shortlist: ShortlistCandidate[];
  agentContext: AgentContext | null;
  connectionState: ConnectionState;
  searchProgress: SearchProgressEntry[];
  sendMessage: (content: string) => Promise<void>;
  conversationId: number | null;
  conversationTitle: string | null;
  shortlistId: number | null;
  isLoading: boolean;
  error: string | null;
  loadConversation: (id: number) => Promise<void>;
  startNewConversation: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRecruiterChat(): UseRecruiterChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [shortlist, setShortlist] = useState<ShortlistCandidate[]>([]);
  const [agentContext, setAgentContext] = useState<AgentContext | null>(null);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("idle");
  const [searchProgress, setSearchProgress] = useState<SearchProgressEntry[]>(
    []
  );
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null
  );
  const [shortlistId, setShortlistId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Abort controller for active stream
  const abortRef = useRef<AbortController | null>(null);
  // Ref for the current assistant message content (avoid stale closures)
  const assistantContentRef = useRef("");

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // -- startNewConversation --------------------------------------------------

  const startNewConversation = useCallback(async () => {
    abortRef.current?.abort();
    // Reset state locally -- no API call until first message
    setConversationId(null);
    setConversationTitle(null);
    setShortlistId(null);
    setMessages([]);
    setShortlist([]);
    setAgentContext(null);
    setSearchProgress([]);
    setConnectionState("idle");
    setError(null);
    setIsLoading(false);
  }, []);

  // -- loadConversation ------------------------------------------------------

  const loadConversation = useCallback(async (id: number) => {
    abortRef.current?.abort();
    setIsLoading(true);
    setError(null);

    try {
      const detail = await getConversation(id);
      setConversationId(detail.id);
      setConversationTitle(detail.title);
      setShortlistId(detail.shortlist_id);
      setAgentContext(detail.agent_context);
      setConnectionState("idle");
      setSearchProgress([]);

      // Filter to user/assistant messages only
      const chatMessages: ChatMessage[] = detail.messages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({
          id: String(m.id),
          role: m.role as "user" | "assistant",
          content: m.content ?? "",
          isStreaming: false,
        }));
      setMessages(chatMessages);

      // Load shortlist items if conversation has a linked shortlist
      if (detail.shortlist_id) {
        try {
          const sl = await getShortlist(detail.shortlist_id);
          const candidates: ShortlistCandidate[] = sl.items.map((item) => ({
            person_entity_id: item.person_entity_id,
            item_id: item.id,
            name: item.name ?? "Unknown",
            headline: item.headline ?? undefined,
            keywords: item.keywords ?? [],
            quality_score: item.quality_score ?? undefined,
            rationale: item.notes ?? undefined,
            rating: item.rating ?? undefined,
          }));
          setShortlist(candidates);
        } catch {
          // Non-critical — shortlist panel will just be empty
          setShortlist([]);
        }
      } else {
        setShortlist([]);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load conversation"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  // -- sendMessage -----------------------------------------------------------

  const sendMessage = useCallback(
    async (content: string) => {
      if (connectionState !== "idle") return;

      const trimmed = content.trim();
      if (!trimmed) return;

      setError(null);

      // Lazy-create conversation on first message
      let activeConversationId = conversationId;
      if (!activeConversationId) {
        try {
          const conv = await createConversation();
          activeConversationId = conv.id;
          setConversationId(conv.id);
          setConversationTitle(conv.title);
          setShortlistId(conv.shortlist_id);
        } catch (err) {
          setError(
            err instanceof Error ? err.message : "Failed to create conversation"
          );
          return;
        }
      }

      // Add user message immediately (optimistic)
      const userMsgId = `user-${Date.now()}`;
      const userMsg: ChatMessage = {
        id: userMsgId,
        role: "user",
        content: trimmed,
      };

      // Add empty assistant message for streaming
      const assistantMsgId = `assistant-${Date.now()}`;
      assistantContentRef.current = "";

      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: assistantMsgId,
          role: "assistant",
          content: "",
          isStreaming: true,
        },
      ]);

      setConnectionState("connecting");
      setSearchProgress([]);

      // Create abort controller for this stream
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await streamMessage(
          activeConversationId,
          trimmed,
          (event, data) => {
            switch (event) {
              case "token": {
                const text = (data.text as string) ?? "";
                assistantContentRef.current += text;
                const currentContent = assistantContentRef.current;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, content: currentContent }
                      : m
                  )
                );
                setConnectionState("streaming");
                break;
              }

              case "status": {
                setConnectionState("streaming");
                break;
              }

              case "search_started": {
                const query = (data.query as string) ?? "";
                setSearchProgress((prev) => [
                  ...prev,
                  { query, status: "searching" },
                ]);
                break;
              }

              case "search_completed": {
                const query = (data.query as string) ?? "";
                const count = (data.count as number) ?? 0;
                setSearchProgress((prev) =>
                  prev.map((p) =>
                    p.query === query
                      ? { ...p, status: "completed" as const, count }
                      : p
                  )
                );
                break;
              }

              case "shortlist_update": {
                const items = (data.items as Record<string, unknown>[]) ?? [];
                const candidates: ShortlistCandidate[] = items.map((item) => ({
                  person_entity_id: item.person_entity_id as number,
                  item_id: item.item_id as number | undefined,
                  name: (item.name as string) ?? "Unknown",
                  headline: item.headline as string | undefined,
                  keywords: item.keywords as string[] | undefined,
                  quality_score: item.quality_score as number | undefined,
                  rationale: item.rationale as string | undefined,
                  rating: item.rating as string | undefined,
                }));
                setShortlist(candidates);
                break;
              }

              case "context_update": {
                const ctx = (data.context as AgentContext) ?? null;
                setAgentContext(ctx);
                // Use the title generated by the backend
                if (data.title) {
                  setConversationTitle(data.title as string);
                }
                break;
              }

              case "done": {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId ? { ...m, isStreaming: false } : m
                  )
                );
                setConnectionState("idle");
                setSearchProgress([]);
                break;
              }

              case "error": {
                const msg = (data.message as string) ?? "An error occurred";
                setError(msg);
                setConnectionState("error");
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId ? { ...m, isStreaming: false } : m
                  )
                );
                break;
              }
            }
          },
          controller.signal
        );
      } catch (err) {
        if ((err as Error).name === "AbortError") {
          // User cancelled -- mark streaming as done
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgId ? { ...m, isStreaming: false } : m
            )
          );
          setConnectionState("idle");
          return;
        }
        setError(
          err instanceof Error ? err.message : "Failed to send message"
        );
        setConnectionState("error");
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId ? { ...m, isStreaming: false } : m
          )
        );
      }
    },
    [conversationId, connectionState]
  );

  return {
    messages,
    shortlist,
    agentContext,
    connectionState,
    searchProgress,
    sendMessage,
    conversationId,
    conversationTitle,
    shortlistId,
    isLoading,
    error,
    loadConversation,
    startNewConversation,
  };
}
